import {
  TicketFilterInput,
  ScheduleFilterInput,
} from './../../hooks/useBookingListHooks';
import { ApolloQueryResult, useQuery, useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { MeData } from 'graphQL/Auth/types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  BookingTicket,
  FixedBookingTicket,
  FixedTicket,
  FixedTicketEdge,
  ScheduleState,
  Ticket,
  TicketEdge,
  TicketOrderType,
  TicketState,
  UnfixedTicket,
} from 'types/api';
import {
  GET_FIXED_SCHEDULE_TICKET_LIST,
  GET_UNFIXED_SCHEDULE_TICKET_LIST,
} from '..';
import { useLocation } from 'react-router-dom';
import { errorHandler } from 'utils/ErrorHandler';
import { fetchAllAtOnce } from 'utils/fetchAllAtOnce';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { BookingTermType } from '../types';

const PAGE_SIZE = 50;

dayjs.locale('ko');

export function useQueryGetBookingTickets() {
  const myInfo: MeData = useUserInformation(state => state.userInfo);
  const location = useLocation();
  const client = useApolloClient();

  const startedAt = new Date();
  startedAt.setMonth(startedAt.getMonth() - 1);
  startedAt.setHours(0, 0, 0, 0);

  const selectedDate = dayjs(new Date());
  const startOfMonth = selectedDate.startOf('month').valueOf();
  const endOfMonth = selectedDate.endOf('month').valueOf();

  const fixedStatusIn = [
    ScheduleState.SOLD_OUT,
    ScheduleState.CANCELED,
    ScheduleState.CLOSED,
    ScheduleState.PAUSED,
    ScheduleState.OPENED,
    ScheduleState.FINISHED,
    // ScheduleState.EDITING,
    // ScheduleState.DELETED,
  ];

  const unfixedStatusIn = [
    TicketState.CONFIRMED,
    TicketState.USED,
    TicketState.ABSENT,
  ];

  const [ticketFilterInput, setTicketFilterInput] = useState<TicketFilterInput>(
    {
      page: 1,
      size: PAGE_SIZE,
      filter: {
        hostId: myInfo.me.host.id,
        unscheduled: true,
        productId: location.state ? location.state.productId : null,
        orderType: TicketOrderType.SCHEDULE_EARLIEST,
        termWithType: {
          term: { startedAt: startOfMonth, endedAt: endOfMonth },
          type: BookingTermType.STARTING,
        },
      },
    },
  );

  const {
    data: unFixedScheduleTicketList,
    refetch: refetchUnFixedScheduleTicketList,
    loading: unFixedScheduleTicketListLoading,
  } = useQuery<BookingTicket>(GET_UNFIXED_SCHEDULE_TICKET_LIST, {
    variables: {
      ...ticketFilterInput,
      filter: {
        ...ticketFilterInput.filter,
        unscheduled: true,
        statusIn: unfixedStatusIn,
      },
    },
    skip: !myInfo?.me?.host?.id,
  });

  const {
    data: fixedScheduleTicketList,
    refetch: refetchFixedScheduleTicketList,
    loading: fixedScheduleTicketListLoading,
  } = useQuery<FixedBookingTicket>(GET_FIXED_SCHEDULE_TICKET_LIST, {
    variables: {
      ...ticketFilterInput,
      filter: {
        // ...ticketFilterInput.filter,
        ...(ticketFilterInput.filter.customerSearchKeyword && {
          customerSearchKeyword: ticketFilterInput.filter.customerSearchKeyword,
        }),
        hostId: myInfo.me.host.id,
        termWithType: {
          ...ticketFilterInput.filter.termWithType,
        },
        statusIn: fixedStatusIn,
      },
    },
    skip: !myInfo?.me?.host?.id,
  });

  // 자동 페이지네이션을 위한 상태
  const [allFixedEdges, setAllFixedEdges] = useState<FixedTicketEdge[]>([]);
  const [allUnfixedEdges, setAllUnfixedEdges] = useState<TicketEdge[]>([]);
  const initialLoadDoneRef = useRef(false);
  const fetchGenerationRef = useRef(0); // 리패치(월/필터 변경) 시 진행 중이던 자동 로드 무효화용

  /*
    첫 페이지 응답을 화면에 바로 표시하고, 다음 페이지가 있으면 전체를 한 번에 다시 받아 교체한다.
    (client.query 사용으로 useQuery 캐시 영향 없음, 페이지 분할 시 누락/중복 이유는 fetchAllAtOnce 참고)
    generation이 바뀌면(새 리패치 시작) 진행 중이던 로드는 결과를 버린다.
  */
  const fetchAllPages = useCallback(
    async (
      generation: number,
      filterInput: TicketFilterInput,
      fixedFirstPage: FixedBookingTicket['product']['schedules'] | undefined,
      unfixedFirstPage: BookingTicket['booking']['tickets'] | undefined,
    ) => {
      // 첫 페이지 데이터 바로 화면에 표시
      setAllFixedEdges([...(fixedFirstPage?.edges || [])]);
      setAllUnfixedEdges([...(unfixedFirstPage?.edges || [])]);

      const isStale = () => fetchGenerationRef.current !== generation;

      try {
        const [unfixedEdges, fixedEdges] = await Promise.all([
          fetchAllAtOnce(unfixedFirstPage, isStale, async (page, size) => {
            const result = await fetchWithRetry(() =>
              client.query<BookingTicket>({
                query: GET_UNFIXED_SCHEDULE_TICKET_LIST,
                variables: {
                  ...filterInput,
                  page,
                  size,
                  filter: {
                    ...filterInput.filter,
                    unscheduled: true,
                    statusIn: unfixedStatusIn,
                  },
                },
                fetchPolicy: 'network-only',
              }),
            );
            return result.data?.booking?.tickets;
          }),
          fetchAllAtOnce(fixedFirstPage, isStale, async (page, size) => {
            const result = await fetchWithRetry(() =>
              client.query<FixedBookingTicket>({
                query: GET_FIXED_SCHEDULE_TICKET_LIST,
                variables: {
                  page,
                  size,
                  filter: {
                    hostId: myInfo.me.host.id,
                    productIds: filterInput.filter?.productId ?? null,
                    termWithType: {
                      ...filterInput.filter.termWithType,
                    },
                    statusIn: fixedStatusIn,
                    ...(filterInput.filter.customerSearchKeyword && {
                      customerSearchKeyword:
                        filterInput.filter.customerSearchKeyword,
                    }),
                  },
                },
                fetchPolicy: 'network-only',
              }),
            );
            return result.data?.product?.schedules;
          }),
        ]);

        if (isStale()) {
          return;
        }

        setAllFixedEdges(fixedEdges);
        setAllUnfixedEdges(unfixedEdges);
      } catch (error) {
        console.error('Error fetching all pages:', error);
      }
    },
    [client, myInfo.me.host.id],
  );

  // 초기 데이터 로드 후 자동으로 나머지 페이지 로드
  useEffect(() => {
    if (
      fixedScheduleTicketList &&
      unFixedScheduleTicketList &&
      !fixedScheduleTicketListLoading &&
      !unFixedScheduleTicketListLoading &&
      !initialLoadDoneRef.current
    ) {
      initialLoadDoneRef.current = true;
      fetchAllPages(
        fetchGenerationRef.current,
        ticketFilterInput,
        fixedScheduleTicketList?.product?.schedules,
        unFixedScheduleTicketList?.booking?.tickets,
      );
    }
  }, [
    fixedScheduleTicketList,
    unFixedScheduleTicketList,
    fixedScheduleTicketListLoading,
    unFixedScheduleTicketListLoading,
    ticketFilterInput,
    fetchAllPages,
  ]);

  const [bookingInfo, setBookingInfo] = useState<{
    totalCount: number;
  }>();
  const [bookingTickets, setBookingTickets] = useState<
    {
      date: string;
      value: (Ticket | FixedTicket)[];
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const convertTimestampToDate = timestamp => {
    return dayjs(timestamp).format('YYYY년 M월 D일 ddd요일');
  };

  // 데이터를 날짜별로 그룹화하는 함수
  const groupDataByDate = (data: (FixedTicketEdge | TicketEdge)[]) => {
    let result = [];
    let tempMap = {};

    for (let idx = 0; idx < data.length; idx++) {
      const isUnfixedTicket = 'scheduleTerm' in data[idx].node ? true : false;

      const dataNode = data[idx].node;
      const dataKey = isUnfixedTicket ? 'scheduleTerm' : 'term';

      const dateString = dataNode[dataKey]
        ? convertTimestampToDate(dataNode[dataKey].startedAt)
        : '';

      // result에서 해당 날짜가 존재하는지 확인
      const existingEntryIndex = result.findIndex(
        item => item.date === dateString,
      );

      if (existingEntryIndex !== -1) {
        // 이미 존재하는 날짜일 경우 value에 새로운 데이터를 추가
        result[existingEntryIndex].value.push(dataNode);
      } else {
        // 존재하지 않는 날짜일 경우 새로운 항목 추가
        result = [
          ...result,
          {
            date: dateString,
            value: [dataNode],
          },
        ];
      }
    }

    return result;
  };

  const handleRefetchBookingTicketFilter = async (
    ticketFilterInput: TicketFilterInput,
  ) => {
    try {
      // 진행 중이던 자동 페이지 로드 무효화 후 처음부터 다시 로드
      const generation = ++fetchGenerationRef.current;

      const [unfixedResponse, fixedResponse] = await Promise.all([
        fetchWithRetry(() =>
          refetchUnFixedScheduleTicketList({
            ...ticketFilterInput,
            page: 1,
            size: PAGE_SIZE,
            filter: {
              ...ticketFilterInput.filter,
              statusIn: unfixedStatusIn,
            },
          }),
        ),
        fetchWithRetry(() =>
          refetchFixedScheduleTicketList({
            page: 1,
            size: PAGE_SIZE,
            filter: {
              // ...ticketFilterInput.filter,
              hostId: myInfo.me.host.id,
              productIds: ticketFilterInput.filter?.productId ?? null,
              termWithType: {
                ...ticketFilterInput.filter.termWithType,
              },
              statusIn: fixedStatusIn,
              ...(ticketFilterInput.filter.customerSearchKeyword && {
                customerSearchKeyword:
                  ticketFilterInput.filter.customerSearchKeyword,
              }),
            },
          }),
        ),
      ]);

      // 이 응답을 기다리는 동안 새 리패치가 시작됐다면 결과를 버림
      if (fetchGenerationRef.current !== generation) {
        return;
      }

      if (fixedResponse || unfixedResponse) {
        setIsLoading(false);

        // 첫 페이지 데이터를 바로 표시하고, 나머지 페이지는 이어서 자동 로드
        fetchAllPages(
          generation,
          ticketFilterInput,
          fixedResponse?.data.product.schedules,
          unfixedResponse?.data.booking.tickets,
        );

        /*
          새로 고침했을때, 스크롤리 내려간 상태여서 사용자들이 헷갈릴 수 있음.
          그래서 최상단으로 스크롤 올림.
        */
        document.body.scrollTo({
          top: 0,
        });
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (fixedScheduleTicketList && unFixedScheduleTicketList) {
      const totalCount =
        (fixedScheduleTicketList?.product?.schedules?.totalCount || 0) +
        (unFixedScheduleTicketList?.booking?.tickets?.totalCount || 0);

      setBookingInfo(pre => ({
        ...pre,
        totalCount,
      }));
    }
  }, [fixedScheduleTicketList, unFixedScheduleTicketList]);

  // 누적된 데이터로 bookingTickets 업데이트
  useEffect(() => {
    setBookingTickets(groupDataByDate([...allFixedEdges, ...allUnfixedEdges]));
  }, [allFixedEdges, allUnfixedEdges]);

  return {
    bookingTickets,
    ticketFilterInput,
    loading:
      unFixedScheduleTicketListLoading ||
      fixedScheduleTicketListLoading,
    isLoading,
    bookingInfo,
    setIsLoading,
    setTicketFilterInput,
    handleRefetchBookingTicketFilter,
  };
}
